import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';
import { FilterValues } from '@/hooks/use-filter-values';
import { DEFAULT_PAGE_SIZE_PLANNER_ASSIGNMENTS } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common';
import { GroupBy } from '@/modules/common/types';
import { CursorPaginationParametersRequest } from '@/modules/common/types/pagination-types';
import { logger } from '@/utils/logger/logger';

import {
  PLANNER_PAGINATION_LOG_TAG,
  PLANNER_QUERY_GC_TIME_MS,
  QUARTER_IN_MONTHS,
} from '../../constants';
import {
  DualAxisPageParam,
  fetchNextDateRange as fetchNextDateRangeHelper,
  fetchPreviousDateRange as fetchPreviousDateRangeHelper,
  PageResponseWithMetadata,
} from '../../helpers/planner-base/planner-pagination-helpers';
import { mapUtilizationBasedOnToDTO, mapUtilizationFiltersToDTO } from '../../mappers';
import { mapAssignmentFilters } from '../../mappers/map-assignment-filters';
import usePlannerStore from '../../store';

interface UseAssignmentsBaseParams {
  filters: FilterValues['values'];
  enabled?: boolean;
}

/** Common shape for all planner assignment API request params */
export interface AssignmentsApiFnParams {
  filters: ReturnType<typeof mapAssignmentFilters>;
  utilizationBasedOn: ReturnType<typeof mapUtilizationBasedOnToDTO>;
  utilizationFilters?: ReturnType<typeof mapUtilizationFiltersToDTO>;
  /** When true, placeholders are ignored when calculating group-level utilization (department/project) */
  ignorePlaceholdersForGroup?: boolean;
  pagination: CursorPaginationParametersRequest;
}

/**
 * Configuration for the generic assignments base hook.
 * Each groupBy variant (member, project, department) provides its own config.
 */
interface UseAssignmentsBaseConfig<TItem> {
  /** Which GroupBy key to use for store subscriptions (date ranges, scroll triggers) */
  groupByKey: GroupBy;
  /** Query key prefix for TanStack Query (e.g., 'assignments-by-member') */
  queryKeyPrefix: string;
  /** Log tag for debug messages (e.g., 'useAssignmentsByMember') */
  logTag: string;
  /** API function to fetch assignments */
  apiFn: (params: AssignmentsApiFnParams) => Promise<PaginatedResponse<TItem>>;
  /**
   * Transform raw infinite query pages into merged, deduplicated items + pagination.
   * Each hook implements its own merge strategy (flat members vs nested group→members).
   */
  selectFn: (data: InfiniteData<PageResponseWithMetadata<TItem>>) => PaginatedResponse<TItem>;
  /**
   * Build a content-level fingerprint for cache invalidation.
   * Must change when mutations patch the cache so that select() bypasses its memoization.
   */
  buildFingerprint: (data: InfiniteData<PageResponseWithMetadata<TItem>>) => string;
  /**
   * Optional weight function for member-weighted horizontal pagination.
   * For project/department grouping, returns the member count of each group item.
   */
  getItemWeight?: (item: TItem) => number;
}

/**
 * Generic base hook for fetching planner assignments with dual-axis infinite scroll.
 * Encapsulates all shared logic:
 * - Store subscriptions (date ranges, planner preferences, scroll triggers)
 * - Date range initialization + fallback
 * - useInfiniteQuery with queryFn, getNextPageParam, getPreviousPageParam
 * - Fingerprint-based select() caching
 * - Scroll-to-date reset effect with debounce
 * - fetchNextDateRange / fetchPreviousDateRange callbacks
 *
 * Each groupBy variant provides its own selectFn, buildFingerprint, and optional getItemWeight.
 */
export function useAssignmentsBase<TItem>(
  { filters, enabled = true }: UseAssignmentsBaseParams,
  config: UseAssignmentsBaseConfig<TItem>
) {
  const { groupByKey, queryKeyPrefix, logTag, apiFn, selectFn, buildFingerprint, getItemWeight } =
    config;

  const queryClient = useQueryClient();
  const queryDateRange = usePlannerStore(state => state.queryDateRanges[groupByKey]);
  const plannerPreferences = usePlannerStore(state => state.plannerPreferences);
  const utilizationBasedOn = plannerPreferences.utilizationBasedOn;
  const utilizationFilters = plannerPreferences;
  const scrollToDateTrigger = usePlannerStore(state => state.scrollToDateTriggers[groupByKey]);
  const today = dayjs();

  // Use queryDateRange from store, fallback to default ±3 months.
  // When falling back, also persist to the store so that arrow navigation
  // and the visible-date-centric pagination logic can compare against it.
  const startDate =
    queryDateRange?.startDate || today.subtract(QUARTER_IN_MONTHS, 'month').format(DATE_KEY_FORMAT);
  const endDate =
    queryDateRange?.endDate || today.add(QUARTER_IN_MONTHS, 'month').format(DATE_KEY_FORMAT);

  useEffect(() => {
    if (!queryDateRange && enabled) {
      const state = usePlannerStore.getState();
      usePlannerStore.setState({
        queryDateRanges: { ...state.queryDateRanges, [groupByKey]: { startDate, endDate } },
      });
    }
    // Only run on mount / when queryDateRange becomes null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryDateRange, enabled]);

  const utilizationBasedOnDTO = mapUtilizationBasedOnToDTO(utilizationBasedOn);
  const utilizationFiltersDTO = mapUtilizationFiltersToDTO(utilizationFilters);

  const queryKey = useMemo(
    () => [
      queryKeyPrefix,
      filters,
      {
        utilizationBasedOn: plannerPreferences.utilizationBasedOn,
        assignmentStatuses: plannerPreferences.assignmentStatuses,
        ignorePlaceholdersForGroup: plannerPreferences.ignorePlaceholdersForGroup,
      },
    ],
    [
      filters,
      plannerPreferences.utilizationBasedOn,
      plannerPreferences.assignmentStatuses,
      plannerPreferences.ignorePlaceholdersForGroup,
      queryKeyPrefix,
    ]
  );
  const filtersFingerprint = useMemo(() => JSON.stringify(filters), [filters]);

  // Identity of the active query key (filters + utilization preferences). Prefixed
  // onto the select() memo key so the memo can NEVER be reused across different
  // query keys. Without this, changing a preference that alters only group-level
  // utilization (e.g. ignorePlaceholdersForGroup on dept/project) — but not the
  // member-level data the content fingerprint is built from — would hit the stale
  // memo and the UI wouldn't update until reload.
  const queryKeyFingerprint = useMemo(() => JSON.stringify(queryKey), [queryKey]);

  // Cache the select() result to avoid producing new object references when
  // TanStack Query re-runs select with the same underlying page data (e.g.,
  // when isFetchingNextPage flips). New references cascade through the mapper,
  // store setters, and virtualizer.measure(), causing visible UI jumps.
  const selectCacheRef = useRef<{
    fingerprint: string;
    result: PaginatedResponse<TItem>;
  } | null>(null);

  const query = useInfiniteQuery({
    enabled,
    queryKey,
    // When the query key changes (filters / utilization preferences), keep showing
    // the previous data instead of unmounting the planner into a skeleton. The UI
    // signals the refresh via `isRefreshing` (dimmed, non-interactive planner);
    // the skeleton stays reserved for genuinely empty first loads (initial visit,
    // first switch to a groupBy).
    placeholderData: keepPreviousData,
    // Data is manually managed (resetQueries, horizontal pagination) — never auto-refetch
    // on mount or when enabled toggles back to true after a tab switch.
    staleTime: Infinity,
    // gcTime must match staleTime: the cache holds many manually-merged
    // horizontal pages + optimistic mutation patches. With the default 5-min
    // gcTime, idling/backgrounding (or switching groupBy so this query goes
    // inactive) garbage-collects the whole cache; on return it refetches only the
    // first page for the stored range, so later mutations patch a partial cache
    // and render assignments/utilization incorrectly. See PLANNER_QUERY_GC_TIME_MS.
    gcTime: PLANNER_QUERY_GC_TIME_MS,
    queryFn: async ({ pageParam }) => {
      const { cursor } = pageParam as DualAxisPageParam;
      const apiFilters = mapAssignmentFilters({ startDate, endDate, filters });

      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        logTag +
          ': API CALL ' +
          startDate +
          '..' +
          endDate +
          ' cursor=' +
          cursor +
          ' axis=' +
          (pageParam as DualAxisPageParam).axis +
          ' dir=' +
          (pageParam as DualAxisPageParam).direction
      );

      const response = await apiFn({
        filters: apiFilters,
        utilizationBasedOn: utilizationBasedOnDTO,
        utilizationFilters: utilizationFiltersDTO,
        ignorePlaceholdersForGroup: plannerPreferences.ignorePlaceholdersForGroup,
        pagination: {
          cursor: cursor ?? undefined,
          pageSize: DEFAULT_PAGE_SIZE_PLANNER_ASSIGNMENTS,
        },
      });

      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        logTag +
          ': API RESPONSE items=' +
          response.items?.length +
          ' total=' +
          response.pagination?.total +
          ' cursor=' +
          response.pagination?.cursor
      );

      // Attach metadata to the response for tracking pagination state
      return {
        ...response,
        _dateRange: { startDate, endDate },
        _cursor: cursor,
      } as PageResponseWithMetadata<TItem>;
    },
    initialPageParam: {
      startDate,
      endDate,
      cursor: undefined,
      direction: undefined,
      axis: 'horizontal',
    } as DualAxisPageParam,
    getNextPageParam: (_lastPage, allPages, _lastPageParam, allPageParams) => {
      // Determine next page for vertical scrolling (more items).
      // Identify vertical pages by their pageParam: initial/vertical pages have
      // direction=undefined, horizontal extensions have direction='past'/'future'.
      // We count items from ALL vertical pages (not just initial-range ones) because
      // after horizontal extensions, new vertical pages use the expanded date range.
      const verticalPageIndices = allPageParams
        .map((pp: DualAxisPageParam, i: number) =>
          !pp.direction || pp.axis === 'vertical' ? i : -1
        )
        .filter((i: number) => i >= 0);

      if (verticalPageIndices.length === 0) return undefined;

      const verticalPages = verticalPageIndices.map(i => allPages[i]).filter(Boolean);

      // Get cursor from the last vertical page (most recent vertical fetch)
      const lastVerticalPage = verticalPages[verticalPages.length - 1];

      const loadedItemsCount = verticalPages.reduce((sum, page) => sum + page.items.length, 0);
      const totalItems = lastVerticalPage?.pagination?.total ?? 0;

      if (loadedItemsCount >= totalItems) {
        return undefined;
      }

      const nextCursor = lastVerticalPage?.pagination?.cursor;

      if (!nextCursor) return undefined;

      // Use the last vertical page's date range (may be the expanded range after
      // horizontal extensions — the queryFn uses the current store date range anyway)
      return {
        startDate: lastVerticalPage._dateRange.startDate,
        endDate: lastVerticalPage._dateRange.endDate,
        cursor: nextCursor,
        direction: undefined,
        axis: 'vertical',
      } as DualAxisPageParam;
    },
    getPreviousPageParam: () => {
      // Horizontal left scrolling is handled manually by fetchPreviousDateRange
      // to ensure all cursor pages are fetched for data consistency
      return undefined;
    },
    select: data => {
      // Scope the memo to the query key so it can't bleed across filter/preference
      // changes that leave the content fingerprint unchanged (e.g. a group-level-only
      // utilization change on dept/project).
      const fingerprint = `${queryKeyFingerprint}::${buildFingerprint(data)}`;
      if (selectCacheRef.current?.fingerprint === fingerprint) {
        return selectCacheRef.current.result;
      }

      const result = selectFn(data);
      selectCacheRef.current = { fingerprint, result };
      return result;
    },
  });

  // True while a filter change refetches the trimmed first page in place — the
  // planner keeps showing the previous rows dimmed (shimmer) instead of a skeleton.
  const [isReloadingAfterFilterChange, setIsReloadingAfterFilterChange] = useState(false);

  const restartQueryFromFirstPage = useCallback(
    (
      reason: 'scrollToDate' | 'filters',
      range: { startDate: string; endDate: string } | null | undefined
    ) => {
      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        logTag +
          ': restartQueryFromFirstPage reason=' +
          reason +
          ' range=' +
          range?.startDate +
          '..' +
          range?.endDate
      );

      queryClient.setQueryData<InfiniteData<PageResponseWithMetadata<TItem>, DualAxisPageParam>>(
        queryKey,
        oldData => {
          if (!oldData || oldData.pages.length <= 1) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.slice(0, 1),
            pageParams: oldData.pageParams.slice(0, 1),
          };
        }
      );
      selectCacheRef.current = null;

      if (reason === 'filters') {
        // Refetch in place: the previous rows stay visible (shimmer) while the
        // first page reloads. The queryFn reads startDate/endDate from the hook
        // closure — not from the stored page param — so the refetched page uses
        // the freshly bootstrapped viewport range. A full reset here would drop
        // the query to pending and unmount the planner into a skeleton, which
        // is reserved for initial loads and far date jumps.
        //
        // The shimmer is released from the refetch promise's finally() rather
        // than by observing query.isFetching: isFetching hasn't necessarily
        // flipped true yet on the render that follows this setState, so a
        // released-on-!isFetching effect could clear the flag before the fetch
        // even starts. Tracking the promise avoids that render-timing race.
        setIsReloadingAfterFilterChange(true);
        void queryClient
          .refetchQueries({ queryKey, exact: true })
          .finally(() => setIsReloadingAfterFilterChange(false));
      } else {
        // Far date jumps intentionally reset to pending — the previous rows
        // belong to a viewport months away, so the skeleton is the right UI.
        queryClient.resetQueries({ queryKey, exact: true });
      }
    },
    [logTag, queryClient, queryKey]
  );

  // Reset query when scrollToDate is called (date picker or "Today" button)
  // This clears all pages and fetches only the first page for the new date range.
  // Debounced to prevent flooding the API when the user clicks rapidly.
  // Track last-seen trigger to only react to actual increments, not re-mounts with same value.
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSeenTriggerRef = useRef(scrollToDateTrigger);
  useEffect(() => {
    if (scrollToDateTrigger > lastSeenTriggerRef.current && enabled) {
      lastSeenTriggerRef.current = scrollToDateTrigger;
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        const range = usePlannerStore.getState().queryDateRanges[groupByKey];
        restartQueryFromFirstPage('scrollToDate', range);
      }, 300);
    } else {
      lastSeenTriggerRef.current = scrollToDateTrigger;
    }
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToDateTrigger, enabled]);

  const lastSeenFiltersFingerprintRef = useRef(filtersFingerprint);
  useEffect(() => {
    if (!enabled) {
      lastSeenFiltersFingerprintRef.current = filtersFingerprint;
      return;
    }

    if (filtersFingerprint === lastSeenFiltersFingerprintRef.current) {
      return;
    }

    lastSeenFiltersFingerprintRef.current = filtersFingerprint;

    const cachedData =
      queryClient.getQueryData<InfiniteData<PageResponseWithMetadata<TItem>, DualAxisPageParam>>(
        queryKey
      );

    if (!cachedData) return;

    const firstPageRange = cachedData.pages[0]?._dateRange;
    const cacheAlreadyMatchesCurrentRange =
      firstPageRange?.startDate === startDate &&
      firstPageRange?.endDate === endDate &&
      cachedData.pages.length <= 1;

    if (cacheAlreadyMatchesCurrentRange) return;

    // Filter changes should always re-bootstrap around the current visible viewport.
    // If this exact filter key was loaded before, Infinity staleTime would otherwise
    // reuse its old expanded pages and ignore the freshly rewritten queryDateRange.
    restartQueryFromFirstPage('filters', { startDate, endDate });
  }, [
    enabled,
    filtersFingerprint,
    queryClient,
    queryKey,
    restartQueryFromFirstPage,
    startDate,
    endDate,
  ]);

  /**
   * Fetch future dates (right scroll) for horizontal pagination.
   * Fetches all cursor pages that were loaded for the initial date range
   * to ensure we have the same items for the new time period.
   */
  const fetchNextDateRange = useCallback(
    async () =>
      fetchNextDateRangeHelper<PageResponseWithMetadata<TItem>>({
        queryClient,
        queryKey,
        filters,
        plannerPreferences,
        apiFn,
        getItemWeight,
      }),
    [filters, queryClient, queryKey, plannerPreferences, apiFn, getItemWeight]
  );

  /**
   * Fetch past dates (left scroll) for horizontal pagination.
   * Fetches all cursor pages that were loaded for the initial date range
   * to ensure we have the same items for the new time period.
   */
  const fetchPreviousDateRange = useCallback(
    async () =>
      fetchPreviousDateRangeHelper<PageResponseWithMetadata<TItem>>({
        queryClient,
        queryKey,
        filters,
        plannerPreferences,
        apiFn,
        getItemWeight,
      }),
    [filters, queryClient, queryKey, plannerPreferences, apiFn, getItemWeight]
  );

  return {
    ...query,
    // Previous data is being shown while a changed query key (placeholder) or a
    // same-key filter reload refetches — the planner renders dimmed/non-interactive
    // (shimmer) instead of a skeleton in both cases.
    isRefreshing: query.isPlaceholderData || isReloadingAfterFilterChange,
    // Vertical pagination (more items)
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    // Horizontal pagination (past dates - left scroll)
    fetchPreviousDateRange,
    // Horizontal pagination (future dates - right scroll)
    fetchNextDateRange,
  };
}
